// src/common/pages/authorValidation/PasoConfirmacion.tsx
import React, { useEffect, useRef } from "react";
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";
import { FormularioDeCampos } from "./FormularioDeCampos";
import type { VaciarAlCambiar } from "./FormularioDeCampos";
import { soloCampos } from "./datos";
import type { TextosPagina } from "./textos";
import type { CampoConfig, FilaConfirmacion, Idioma, OpcionDeCatalogo, Valores } from "./tipos";

export interface PasoConfirmacionProps {
  /** El contenido del h2 del paso: la pantalla le antepone el paso, para el lector de pantalla. */
  titulo: React.ReactNode;
  /** Lo que va a quedar, de solo vista. */
  filas: FilaConfirmacion[];
  /** Un aviso breve sobre lo que se muestra (el domicilio leído que reemplaza al guardado). */
  aviso?: string;
  /** Si se está modificando: los datos se ven en un formulario, ahí mismo. Lo decide la pantalla. */
  modificando: boolean;
  /** Los campos del formulario para modificar: los mismos datos que se ven. */
  campos: CampoConfig[];
  /** Los que no se pueden dejar vacíos al modificar. */
  obligatorios: readonly string[];
  /** Lo que se vacía al modificar cuando cambia otro dato (el domicilio: ver VaciarAlCambiar). */
  vaciarAlCambiar?: VaciarAlCambiar;
  /** Con lo que arranca el formulario: lo que se ve. */
  valores: Valores;
  catalogos: Record<string, OpcionDeCatalogo[]>;
  idioma: Idioma;
  textos: TextosPagina;
  ocupado: boolean;
  /** Lo que se espera mientras `ocupado` (la entrega de la foto o el envío de los datos): nombra al botón. */
  textoEspera: string;
  /** Lo que el server rechazó, por campo: el formulario lo muestra en su lugar. */
  erroresDelServidor?: Record<string, string>;
  /**
   * El motivo de una entrega de la foto que falló. Con los datos a la vista va con «Reintentar», que
   * vuelve a confirmar lo que se ve; modificando va solo, y el reintento es el «Confirmar mis datos»
   * del formulario, que manda lo que se ve ahora.
   */
  fallaDeEntrega?: string;
  /** Lo guardado del autor tal como llega (`entityData`): lo que no se cambia no se valida (FormularioDeCampos). */
  guardado?: Record<string, unknown>;
  /** «Confirmar mis datos» con los datos a la vista: se envía lo que se ve. */
  onConfirmar: () => void;
  /** «Modificar mis datos». */
  onModificar: () => void;
  /** «Confirmar mis datos» del formulario: lo que quedó en sus campos, ya validado. */
  onModificados: (valores: Valores) => void;
}

/**
 * El resumen final (KAN-462): todos los datos, de solo vista, y dos salidas. «Confirmar mis datos»
 * los envía tal cual; «Modificar mis datos» los vuelve editables ahí mismo, y el «Confirmar mis
 * datos» del formulario los envía. Lo modificado no lleva tic por lo que diga esta pantalla: el tic
 * lo decide el server comparando con su propia lectura de la foto.
 */
export const PasoConfirmacion: React.FC<PasoConfirmacionProps> = ({
  titulo,
  filas,
  aviso,
  modificando,
  campos,
  obligatorios,
  vaciarAlCambiar,
  valores,
  catalogos,
  idioma,
  textos,
  ocupado,
  textoEspera,
  erroresDelServidor,
  fallaDeEntrega,
  guardado,
  onConfirmar,
  onModificar,
  onModificados,
}) => {
  // Al pasar a modificar, o al volver a la vista, el botón que tenía el foco desaparece: el foco va
  // al título, no al primer campo (en un teléfono abriría el teclado). Solo cuando cambia, no al
  // montar: se compara con el valor anterior, que aguanta el doble montaje de StrictMode.
  const titular = useRef<HTMLHeadingElement>(null);
  const modificandoAntes = useRef(modificando);
  useEffect(() => {
    if (modificando !== modificandoAntes.current) titular.current?.focus();
    modificandoAntes.current = modificando;
  }, [modificando]);

  return (
    <Box>
      <Typography ref={titular} tabIndex={-1} variant="h6" component="h2" sx={{ mb: 2 }}>
        {titulo}
      </Typography>
      {modificando ? (
        <FormularioDeCampos
          campos={campos}
          obligatorios={obligatorios}
          vaciarAlCambiar={vaciarAlCambiar}
          valoresIniciales={valores}
          catalogos={catalogos}
          idioma={idioma}
          textos={textos}
          textoBoton={textos.confirmarMisDatos}
          textoEspera={textoEspera}
          ocupado={ocupado}
          erroresDelServidor={erroresDelServidor}
          guardado={guardado}
          onEnviar={(v) => onModificados(soloCampos(v, campos.map((c) => c.name)))}
        />
      ) : (
        <>
          {aviso && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {aviso}
            </Alert>
          )}
          <Box component="dl" sx={{ m: 0, mb: 3 }}>
            {filas.map((fila) => (
              <Box key={fila.campo} sx={{ py: 1, borderBottom: 1, borderColor: "divider" }}>
                <Typography component="dt" variant="caption" color="text.secondary">
                  {fila.etiqueta}
                </Typography>
                <Typography component="dd" variant="body1" sx={{ m: 0, fontWeight: 500 }}>
                  {fila.texto}
                </Typography>
              </Box>
            ))}
          </Box>
          {/* Ocupado, el botón se queda sin texto y lo nombra la espera (ver FormularioDeCampos). */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={ocupado}
            aria-label={ocupado ? textoEspera : undefined}
            onClick={() => onConfirmar()}
          >
            {ocupado ? <CircularProgress size={24} color="inherit" aria-label={textoEspera} /> : textos.confirmarMisDatos}
          </Button>
          <Button fullWidth sx={{ mt: 1 }} disabled={ocupado} onClick={() => onModificar()}>
            {textos.modificarMisDatos}
          </Button>
        </>
      )}
      {/* Modificando no hay «Reintentar»: mandaría lo del intento que falló, no lo que se ve. */}
      {fallaDeEntrega && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          action={
            modificando ? undefined : (
              <Button color="inherit" size="small" onClick={() => onConfirmar()}>
                {textos.reintentar}
              </Button>
            )
          }
        >
          {fallaDeEntrega}
        </Alert>
      )}
    </Box>
  );
};
